// File: src/hooks/usePageTracking.ts
import { useEffect, useRef } from 'react';
import { useRouter, ParsedLocation } from '@tanstack/react-router';
import { useTracking } from '../tracking/context';
import { useShopWhenAvailable } from '../providers/ShopProvider';

const NON_SHOP_ROUTE_PREFIXES = new Set([
  'invite',
  'lookup-order',
  'marketplace',
  'order',
  'pre-registration',
  'request-guestlist',
  'shared-ticket',
  't',
  'ticket-share',
]);

export const usePageTracking = () => {
  const router = useRouter();
  const { trackEvent } = useTracking();
  const shopContext = useShopWhenAvailable();
  const initialPathRef = useRef(window.location.pathname);

  const isShopRoute = (pathname: string): boolean => {
    const firstSegment = pathname.split('/').filter(Boolean)[0];

    if (!firstSegment) {
      return false;
    }

    return !NON_SHOP_ROUTE_PREFIXES.has(firstSegment);
  };

  useEffect(() => {
    if (!isShopRoute(router.state.location.pathname)) {
      trackPageView(router.state.location);
    }

    return router.subscribe('onResolved', () => {
      // Only track if the path has changed from the initial load
      if (
        router.state.location.pathname !== initialPathRef.current &&
        !isShopRoute(router.state.location.pathname)
      ) {
        trackPageView(router.state.location);
      }
    });
  }, [router, trackEvent]);

  const getShopIdFromUrl = (): string => {
    // First try to get from context
    if (shopContext?.shopSlug) {
      return shopContext.shopSlug;
    }

    // Fallback to URL parsing
    const pathParts = window.location.pathname.split('/');
    // Assuming the shop slug is the first part of the path after the initial slash
    return pathParts[1] || 'unknown';
  };

  const trackPageView = (location: ParsedLocation) => {
    const route = location.pathname;
    const params = location.search;

    trackEvent({
      type: 'PAGE_VIEW',
      payload: {
        shopId: getShopIdFromUrl(),
        path: route,
        title: document.title,
        referrer: document.referrer,
        params: params
      }
    });
  };
};
